import { useRouteError, isRouteErrorResponse, Link } from 'react-router';
import { AlertTriangle as ExclamationTriangleIcon, Home as HomeIcon, RotateCw as ArrowPathIcon } from 'lucide-react';

interface ErrorPageProps {
  title?: string;
  message?: string;
  showHomeButton?: boolean;
  showReloadButton?: boolean;
  children?: React.ReactNode;
}

export function ErrorPage({
  title = 'Oops! Something went wrong',
  message = 'An unexpected error occurred. Please try again later.',
  showHomeButton = true,
  showReloadButton = true,
  children
}: ErrorPageProps) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="flex flex-col items-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-error-100">
              <ExclamationTriangleIcon className="h-6 w-6 text-error-600" />
            </div>
            <h1 className="mt-4 text-2xl font-bold text-gray-900 text-center">
              {title}
            </h1>
            <p className="mt-2 text-sm text-gray-600 text-center">
              {message}
            </p>
            
            {children}
            
            <div className="mt-6 flex gap-3">
              {showHomeButton && (
                <Link
                  to="/"
                  className="btn btn-outline"
                >
                  <HomeIcon className="h-4 w-4 mr-2" />
                  Go Home
                </Link>
              )}
              {showReloadButton && (
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-primary"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RootErrorBoundary() {
  const error = useRouteError();
  console.error('Root error boundary:', error);

  // Never redirect or rethrow - always render
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <ErrorPage
          title="404 - Page Not Found"
          message="The page you're looking for doesn't exist."
          showReloadButton={false}
        />
      );
    }

    if (error.status === 401) {
      return (
        <ErrorPage
          title="Authentication Required"
          message="Please log in to access this page."
          showReloadButton={false}
        >
          <Link
            to="/login"
            className="mt-4 btn btn-primary"
          >
            Go to Login
          </Link>
        </ErrorPage>
      );
    }

    if (error.status === 403) {
      return (
        <ErrorPage
          title="Access Denied"
          message="You don't have permission to access this resource."
        />
      );
    }

    if (error.status === 500) {
      return (
        <ErrorPage
          title="Server Error"
          message="We're experiencing technical difficulties. Please try again later."
        />
      );
    }

    return (
      <ErrorPage
        title={`Error ${error.status}`}
        message={error.data?.message || error.statusText || 'An error occurred while processing your request.'}
      />
    );
  }

  // For non-Response errors, just render without rethrow
  return (
    <ErrorPage
      title="Unexpected Error"
      message="An unexpected error occurred. Our team has been notified."
    />
  );
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  console.error('Route error boundary:', error);

  if (isRouteErrorResponse(error)) {
    return (
      <div className="rounded-lg bg-error-50 border border-error-200 p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-5 w-5 text-error-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-error-800">
              {error.status === 404 ? 'Not Found' : `Error ${error.status}`}
            </h3>
            <div className="mt-2 text-sm text-error-700">
              <p>{error.data?.message || error.statusText}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={() => window.location.reload()}
                className="text-sm font-medium text-error-600 hover:text-error-500"
              >
                Try again →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-error-50 border border-error-200 p-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-error-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-error-800">
            Something went wrong
          </h3>
          <div className="mt-2 text-sm text-error-700">
            <p>An unexpected error occurred. Please try refreshing the page.</p>
          </div>
          <div className="mt-4">
            <button
              onClick={() => window.location.reload()}
              className="text-sm font-medium text-error-600 hover:text-error-500"
            >
              Refresh page →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}